import api from './api';

// Prevents page bloat: any image stored as a base64 data-URL inside block
// content (e.g. pasted/dropped directly into an image field) is uploaded to the
// Media Library and replaced with the small hosted URL before the page is saved.
// This keeps the saved payload tiny so it never hits proxy request-body limits.

const isDataImage = (v) => typeof v === 'string' && v.startsWith('data:image');

const extToMime = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/webp': 'webp',
  'image/gif': 'gif', 'image/svg+xml': 'svg', 'image/avif': 'avif', 'image/bmp': 'bmp',
};

async function uploadDataUrl(dataUrl) {
  const blob = await (await fetch(dataUrl)).blob();
  const mime = (blob.type || 'image/png').toLowerCase();
  const ext = extToMime[mime] || 'png';
  const file = new File([blob], `pasted-${Date.now()}.${ext}`, { type: mime });
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data.url;
}

// Returns { blocks, uploaded } where `blocks` has all base64 images replaced by
// hosted URLs. Identical data-URLs are uploaded only once. On any upload failure
// the original value is kept (save proceeds; it just won't be shrunk).
export async function extractBase64Images(blocks) {
  if (!Array.isArray(blocks)) return { blocks, uploaded: 0 };

  const cache = new Map();
  let uploaded = 0;

  const resolve = async (dataUrl) => {
    if (cache.has(dataUrl)) return cache.get(dataUrl);
    try {
      const url = await uploadDataUrl(dataUrl);
      cache.set(dataUrl, url);
      uploaded += 1;
      return url;
    } catch (e) {
      cache.set(dataUrl, dataUrl); // don't retry this one
      return dataUrl;
    }
  };

  const walk = async (node) => {
    if (Array.isArray(node)) {
      const out = [];
      for (const item of node) out.push(await walk(item));
      return out;
    }
    if (node && typeof node === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(node)) {
        out[k] = isDataImage(v) ? await resolve(v) : await walk(v);
      }
      return out;
    }
    return node;
  };

  const cleaned = await walk(blocks);
  return { blocks: cleaned, uploaded };
}
